import { Fragment, useEffect, useState } from "react";
import { Form, FormLoader } from "../../commons/Form/Form";
import {
  Setting,
  SettingDisplay,
  SettingList,
  SettingModder,
} from "../Common/Settings";
import { useTranslation } from "react-i18next";
import Button from "../../commons/Button/Button";
import api from "../../../api/resources";
import { useAuth } from "../../../auth/AuthContext";
import { Cancel, Upload } from "@mui/icons-material";
import { useModalDispatch } from "../../commons/Modal/ModalContext";
import ImageContainer from "../../commons/ImageContainer/ImageContainer";

export default function ThemeSettings() {
  const { t } = useTranslation(["common", "settings"]);
  document.title = t("settings:tabs.theme.title");
  const [loading, setLoading] = useState(true);
  const { activeCompany, loadingActiveCompany } = useAuth();
  const dispatchModal = useModalDispatch();

  const [colors, setColors] = useState(null);

  const initialColors = {
    primary: {
      initialValue: activeCompany?.colours?.primary ?? "",
      value: "",
    },
    secondary: {
      initialValue: activeCompany?.colours?.secondary ?? "",
      value: "",
    },
    tertiary: {
      initialValue: activeCompany?.colours?.tertiary ?? "",
      value: "",
    },
  };

  const init = () => {
    setLoading(true);
    if (!loadingActiveCompany) {
      setColors(initialColors);
    }
    setLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  const handleColorChange = (colorKey, colorValue) => {
    setColors((prev) => ({
      ...prev,
      [colorKey]: {
        ...prev[colorKey],
        value: colorValue,
      },
    }));
  };

  const handleConfirm = async (colorKey, colorValue) => {
    const response = await api.updateActiveCompany({
      colours: {
        [colorKey]: colorValue,
      },
    });

    if (response.ok) {
      setColors((prev) => ({
        ...prev,
        [colorKey]: {
          initialValue: colorValue,
          value: "",
        },
      }));
      window.location.href = "";
    }
  };

  const logoImportModal = {
    title: "",
    component: <LogoImportForm />,
    onSuccess: () => (window.location.href = ""),
  };

  if (loading || loadingActiveCompany) return <FormLoader />;
  return (
    <SettingList>
      {Object.keys(colors).map((colorKey) => (
        <Setting key={colorKey}>
          <SettingDisplay
            label={t(`settings:tabs.theme.items.${colorKey}`)}
            value={colors[colorKey].value || colors[colorKey].initialValue}
          />
          <ColorInput
            initialValue={colors[colorKey].initialValue}
            onChange={(value) => handleColorChange(colorKey, value)}
            onConfirm={(value) => handleConfirm(colorKey, value)}
          />
        </Setting>
      ))}

      <Setting>
        <SettingDisplay
          label={t("settings:tabs.theme.items.logo")}
          value={!activeCompany.logo && t("settings:tabs.theme.items.logoHint")}
        />
        {!activeCompany.logo ? (
          <SettingModder
            icon={<Upload />}
            action={"import"}
            modal={logoImportModal}
          />
        ) : (
          <Button
            style={{ width: "5rem" }}
            onClick={() => dispatchModal({ type: "add", ...logoImportModal })}
          >
            <ImageContainer src={activeCompany.logo} />
          </Button>
        )}
      </Setting>
    </SettingList>
  );
}

function ColorInput({ initialValue, onChange, onConfirm }) {
  const { t } = useTranslation(["common", "settings"]);

  const [color, setColor] = useState(initialValue);

  const handleChange = (value) => {
    setColor(value);
    onChange(value);
  };

  return (
    <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
      <input
        style={{
          height: "2.5rem",
          width: "3rem",
          appearance: "none",
          border: "unset",
        }}
        type="color"
        value={color}
        onChange={({ target: { value } }) => handleChange(value)}
      />
      {color && color !== initialValue && (
        <Fragment>
          <Button
            variant={"primary_a"}
            text={t("common:confirm")}
            onClick={() => onConfirm(color)}
          />
          <Button
            icon={<Cancel />}
            title={t("common:cancel")}
            onClick={() => handleChange(initialValue)}
          />
        </Fragment>
      )}
    </div>
  );
}

function LogoImportForm() {
  const { t } = useTranslation(["common", "financing"]);
  const dispatchModal = useModalDispatch();

  const [files, setFiles] = useState([]);

  const inputs = [
    {
      data: [
        {
          type: "file",
          value: files,
          onChange: setFiles,
          nbFiles: 1,
          fileFormats: ["image/png", "image/jpeg", "image/svg+xml"],
          maxSizeInMB: 1,
        },
      ],
    },
  ];

  const handleSubmit = async () => {
    const data = new FormData();
    data.append("logo", files[0]);
    const response = await api.updateActiveCompany(data, true);
    if (response.ok) {
      dispatchModal({ type: "success" });
      window.location.href = "";
    }
  };

  const form = {
    inputs: inputs,
    button: t("vault:common.fileDrop.upload"),
    onSubmit: handleSubmit,
    isButtonDisabled: files.length === 0,
    className: "FileDropForm",
  };
  return <Form {...form} />;
}
